/**
 * Swiper Slider Web Component
 *
 * A reusable swiper slider component that wraps Swiper.js
 * Supports namespacing for multiple instances with different styles
 *
 * Usage:
 * <swiper-slider
 *   data-namespace="ingredients"
 *   data-slides-per-view-mobile="2"
 *   data-slides-per-view-tablet="3"
 *   data-slides-per-view-desktop="4"
 *   data-space-between="20"
 *   data-pagination="true"
 *   data-navigation="false"
 *   data-loop="false"
 *   data-autoplay="false"
 *   data-free-mode="true"
 * >
 *   <div class="swiper">
 *     <div class="swiper-wrapper">
 *       <div class="swiper-slide">...</div>
 *     </div>
 *     <div class="swiper-pagination"></div>
 *     <div class="swiper-button-prev"></div>
 *     <div class="swiper-button-next"></div>
 *   </div>
 * </swiper-slider>
 */

class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.resizeObserver = null;
  }

  connectedCallback() {
    // Wait for Swiper to be loaded
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper not loaded, waiting...');
      document.addEventListener('DOMContentLoaded', () => this.init());
      return;
    }
    this.init();
  }

  disconnectedCallback() {
    this.destroy();
  }

  init() {
    const swiperContainer = this.querySelector('.swiper');
    if (!swiperContainer) {
      console.warn('SwiperSlider: No .swiper container found');
      return;
    }

    const namespace = this.dataset.namespace || 'default';
    const config = this.getConfig();

    // Add namespace class to container
    this.classList.add(`swiper-slider--${namespace}`);

    // Initialize Swiper
    this.swiper = new Swiper(swiperContainer, config);

    // Handle dynamic visibility updates for pagination
    this.updatePaginationVisibility();

    // Watch for resize to update pagination visibility
    this.resizeObserver = new ResizeObserver(() => {
      this.updatePaginationVisibility();
    });
    this.resizeObserver.observe(this);
  }

  getConfig() {
    const slidesPerViewMobile = parseFloat(this.dataset.slidesPerViewMobile) || 'auto';
    const slidesPerViewTablet = parseFloat(this.dataset.slidesPerViewTablet) || slidesPerViewMobile;
    const slidesPerViewDesktop = parseFloat(this.dataset.slidesPerViewDesktop) || slidesPerViewTablet;
    const spaceBetween = parseInt(this.dataset.spaceBetween) || 20;
    const spaceBetweenMobile = parseInt(this.dataset.spaceBetweenMobile) || spaceBetween;
    const showPagination = this.dataset.pagination !== 'false';
    const showNavigation = this.dataset.navigation === 'true';
    const loop = this.dataset.loop === 'true';
    const autoplay = this.dataset.autoplay === 'true';
    const freeMode = this.dataset.freeMode !== 'false';
    const centeredSlides = this.dataset.centeredSlides === 'true';
    const grabCursor = this.dataset.grabCursor !== 'false';

    const config = {
      slidesPerView: slidesPerViewMobile,
      spaceBetween: spaceBetweenMobile,
      grabCursor: grabCursor,
      freeMode: freeMode,
      loop: loop,
      watchOverflow: true,
      centeredSlides: centeredSlides,
      touchStartPreventDefault: false,
      threshold: 5,
      breakpoints: {
        750: {
          slidesPerView: slidesPerViewTablet,
          spaceBetween: spaceBetween,
        },
        990: {
          slidesPerView: slidesPerViewDesktop,
          spaceBetween: spaceBetween,
        }
      }
    };

    // Pagination (dots)
    if (showPagination) {
      const paginationEl = this.querySelector('.swiper-pagination');
      if (paginationEl) {
        config.pagination = {
          el: paginationEl,
          clickable: true,
          dynamicBullets: this.dataset.dynamicBullets === 'true',
        };
      }
    }

    // Scrollbar
    const showScrollbar = this.dataset.scrollbar === 'true';
    if (showScrollbar) {
      const scrollbarEl = this.querySelector('.swiper-scrollbar');
      if (scrollbarEl) {
        config.scrollbar = {
          el: scrollbarEl,
          draggable: true,
          hide: false,
        };
      }
    }

    // Navigation
    if (showNavigation) {
      const prevEl = this.querySelector('.swiper-button-prev');
      const nextEl = this.querySelector('.swiper-button-next');
      if (prevEl && nextEl) {
        config.navigation = {
          nextEl: nextEl,
          prevEl: prevEl,
        };
      }
    }

    // Autoplay
    if (autoplay) {
      config.autoplay = {
        delay: parseInt(this.dataset.autoplayDelay) || 5000,
        disableOnInteraction: false,
      };
    }

    return config;
  }

  updatePaginationVisibility() {
    if (!this.swiper) return;

    const paginationEl = this.querySelector('.swiper-pagination');
    if (!paginationEl) return;

    // Hide pagination if all slides fit without scrolling
    const slidesCount = this.swiper.slides.length;
    const slidesPerView = this.swiper.params.slidesPerView;

    // Calculate effective slides per view (handle 'auto')
    let effectiveSlidesPerView = slidesPerView;
    if (slidesPerView === 'auto') {
      const containerWidth = this.swiper.width;
      const slideWidth = this.swiper.slides[0]?.offsetWidth || 100;
      effectiveSlidesPerView = Math.floor(containerWidth / slideWidth);
    }

    if (slidesCount <= effectiveSlidesPerView) {
      paginationEl.style.display = 'none';
    } else {
      paginationEl.style.display = '';
    }
  }

  destroy() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  // Public API
  slideNext() {
    this.swiper?.slideNext();
  }

  slidePrev() {
    this.swiper?.slidePrev();
  }

  slideTo(index) {
    this.swiper?.slideTo(index);
  }

  update() {
    this.swiper?.update();
    this.updatePaginationVisibility();
  }
}

// Register the custom element
if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}
